// <Timeline> — canonical ClientHub kit component (Epic 1 · S1.4).
//
// Renders the fn_timeline event stream: { subject, events[], available_types[],
// next_before_seq }. Each event: seq · at · type · label · source · actor ·
// actor_type · scope · summary · payload · says — render `says` verbatim.
// Paginate with next_before_seq (a sequence cursor, not a timestamp).
// available_types offers real filters without a second query. Reusable across
// subjects via the `subjectType` prop (client · task · thread · person); used by
// <ClientSlideOut> and the Client page.
//
// RPC shape confirmed live 2026-09-09. Renders defensively.

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getTimeline } from "../../../api/hub";
import { ErrorState } from "../../shared/StateMessage";
import { EmptyState } from "./EmptyState";
import Sentence from "./Sentence";
import { formatWhen } from "../../shared/format";
import { TINT, tintClasses } from "../../shared/tints";
import { matchesQuery, normalizeQuery } from "../../shared/textMatch";
import { signalTint } from "../signalTint";
import { actorTone, ageTone, messageCountTone, typeTone, TONE_CLASS } from "../metaTone";

const PAGE_SIZE = 50;
// How many more events a "Load more" click reveals when `initialVisible` is set.
const REVEAL_STEP = 5;

// The keys an object-valued field may carry its readable text under, in order.
const TEXT_KEYS = ["text", "name", "display_name", "label"];

/**
 * A backend field as text a row can safely print. Strings and numbers pass through; an
 * object with a readable text/name/label uses it; anything else (null, a boolean, an array,
 * any other object) is empty. React throws on an object child, and one odd event would
 * otherwise take the whole Activity card down, so every printed field goes through this.
 * @param {*} value
 * @returns {string}
 */
export const textOf = (value) => {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (value && typeof value === "object" && !Array.isArray(value)) {
    for (const k of TEXT_KEYS) {
      if (typeof value[k] === "string" && value[k] !== "") return value[k];
    }
  }
  return "";
};

/** Only plain objects are events; a null, string or array entry in `events[]` is skipped. */
const isEvent = (e) => e !== null && typeof e === "object" && !Array.isArray(e);

const TimelineEntry = ({ event }) => {
  const e = event || {};
  const says = textOf(e.says);
  const actor = textOf(e.actor);
  return (
    <li className="flex gap-3 py-2">
      <div className="shrink-0 w-16 text-xs text-gray-400 tabular-nums">
        {formatWhen(e.at)}
      </div>
      <div className="min-w-0 flex-1">
        {says ? (
          <Sentence says={says} tint="cool" size="sm" />
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300">{textOf(e.label) || textOf(e.type)}</p>
        )}
        {actor && <p className="text-xs text-gray-400">{actor}</p>}
      </div>
    </li>
  );
};

/**
 * The tint of an event's dot: a known tint the backend put on the event; else the
 * direction the sentence itself reads in (the same stopgap the attention strip uses, so a
 * "10th follow-up" is amber in both places); else neutral. Never derived from the event
 * type — the set of types is data. An event that matches nothing is calm gray, not an alarm.
 * @param {{tint?:string, says?:string, label?:string}} [event]
 */
export const eventTint = (event) => {
  if (event?.tint && Object.prototype.hasOwnProperty.call(TINT, event.tint)) return event.tint;
  return signalTint({ says: textOf(event?.says), label: textOf(event?.label) });
};

/**
 * The meta line of a stream event as coloured parts: the event's label (or type), a message count when the
 * payload carries one, and the actor. The label takes a stable colour per type, the count its thread-length
 * colour, the actor a colour for AI and the system.
 * @param {object} e  An event.
 * @returns {Array<{text: string, tone: string|null}>}
 */
const streamMetaParts = (e) => {
  const n = e?.payload?.messages;
  const hasCount = Number.isFinite(Number(n)) && n !== null && n !== "";
  const label = textOf(e?.label) || textOf(e?.type);
  const actor = textOf(e?.actor);
  return [
    label ? { text: label, tone: typeTone(textOf(e?.type) || label), upper: true } : null,
    hasCount ? { text: `${Number(n)} ${Number(n) === 1 ? "message" : "messages"}`, tone: messageCountTone(n), upper: true } : null,
    actor ? { text: actor, tone: actorTone(actor, textOf(e?.actor_type)), upper: false } : null,
  ].filter(Boolean);
};

const toneClass = (tone) => (tone ? TONE_CLASS[tone] : undefined);

// The stream variant: events hang off a vertical rail with a tinted dot each, the
// server sentence as the title (verbatim), a quiet meta line and the date on the
// right. The default variant above is untouched.
const StreamEntry = ({ event, last }) => {
  const e = event || {};
  const parts = streamMetaParts(e);
  return (
    <li className="relative flex gap-4 pb-5 last:pb-0" data-testid="timeline-event" data-tint={eventTint(e)}>
      {!last && <span className="absolute left-[4px] top-4 bottom-0 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />}
      <span className={`relative z-10 mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${tintClasses(eventTint(e)).dot}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 text-sm text-gray-900 first-letter:uppercase dark:text-gray-100">{textOf(e.says) || textOf(e.label) || textOf(e.type)}</p>
          <time
            data-tone={ageTone(e.at) || undefined}
            className={`shrink-0 text-xs tabular-nums ${toneClass(ageTone(e.at)) || "text-gray-500 dark:text-gray-400"}`}
          >
            {formatWhen(e.at)}
          </time>
        </div>
        {parts.length > 0 && (
          <p className="mt-1 text-[11px] tracking-wide text-gray-500 dark:text-gray-400" title={parts.map((p) => p.text).join(" · ")}>
            {parts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 ? " · " : null}
                <span data-tone={part.tone || undefined} className={`${part.upper ? "uppercase " : ""}${toneClass(part.tone) || ""}`.trim() || undefined}>{part.text}</span>
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    </li>
  );
};

/**
 * @param {object} props
 * @param {"list"|"stream"} [props.variant="list"]  "stream" hangs events off a tinted rail.
 * @param {number} [props.refreshKey]  Reloads the first page when it changes (e.g. after a send).
 * @param {string} [props.query]  Filters the events ALREADY LOADED (text, label, type, actor) and says
 *   how many that is — older, unloaded activity is not searched. Empty = no filter.
 * @param {string} [props.emptyMessage]  Copy for a stream with no events at all.
 * @param {number|null} [props.initialVisible]  Show only the newest this many events at first; each "Load more"
 *   click reveals the next few, and fetches an older page once every loaded event is showing. Null shows every
 *   loaded event, and "Load more" only fetches older ones.
 * @param {boolean} [props.showFilters=true]  Show the event-type filter tags. Hidden, the tags are not drawn but a
 *   selection already made still applies (the caller can see it through `onStats.tagsActive`).
 * @param {HTMLElement|null} [props.filtersHost]  Draw the filter tags inside this element (a box the caller owns)
 *   instead of at the top of the stream.
 * @param {(stats: {loaded: number, hasMore: boolean, tagsActive: boolean}) => void} [props.onStats]  Told how many
 *   events are loaded, whether older ones remain and whether a tag is selected, once the first page has arrived.
 */
export default function Timeline({ subjectType = "client", clientId, limit = PAGE_SIZE, className = "", variant = "list", refreshKey = 0, query = "", emptyMessage = "No recent activity.", initialVisible = null, showFilters = true, filtersHost = null, onStats }) {
  const [status, setStatus] = useState("loading");
  const [events, setEvents] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [activeTypes, setActiveTypes] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [visible, setVisible] = useState(initialVisible);

  // `silent` reloads the first page in place (after a send) — no skeleton flash, and
  // a failure keeps what is already on screen instead of replacing it with an error.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setStatus("loading");
    try {
      const res = await getTimeline(subjectType, clientId, { limit });
      const data = res?.data || {};
      setEvents(Array.isArray(data.events) ? data.events.filter(isEvent) : []);
      setAvailableTypes(Array.isArray(data.available_types) ? data.available_types.map(textOf).filter(Boolean) : []);
      setNextBefore(data.next_before_seq || null);
      setStatus("ready");
    } catch (err) {
      if (!silent) setStatus("error");
    }
  }, [subjectType, clientId, limit]);

  useEffect(() => { load({ silent: refreshKey > 0 }); }, [load, refreshKey]);

  const loadMore = useCallback(async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const res = await getTimeline(subjectType, clientId, { limit, beforeSeq: nextBefore });
      const data = res?.data || {};
      setEvents((prev) => [...prev, ...(Array.isArray(data.events) ? data.events.filter(isEvent) : [])]);
      setNextBefore(data.next_before_seq || null);
    } catch (err) {
      // The first page stays; say the older page failed rather than looking like the end.
      setLoadMoreFailed(true);
    } finally { setLoadingMore(false); }
  }, [subjectType, clientId, limit, nextBefore, loadingMore]);

  const tagsActive = !!(activeTypes && activeTypes.length > 0);

  // Tell the card how many events are loaded (and whether more exist) once the first page is in.
  useEffect(() => {
    if (status === "ready") onStats?.({ loaded: events.length, hasMore: !!nextBefore, tagsActive });
  }, [status, events.length, nextBefore, tagsActive, onStats]);

  // A new filter or search starts the reveal over from the first few events.
  useEffect(() => { setVisible(initialVisible); }, [activeTypes, query, initialVisible]);

  const byType = activeTypes && activeTypes.length > 0
    ? events.filter((e) => activeTypes.includes(e.type)) : events;
  const searching = normalizeQuery(query) !== "";
  const filtered = searching
    ? byType.filter((e) => matchesQuery(query, textOf(e.says), textOf(e.label), textOf(e.type), textOf(e.actor)))
    : byType;

  const shown = visible == null ? filtered : filtered.slice(0, visible);
  const hiddenCount = filtered.length - shown.length;
  const canFetchOlder = !!nextBefore && !tagsActive;
  // "Load more": reveal the next few already-loaded events; once they are all showing, fetch an older page.
  const onLoadMore = () => {
    if (hiddenCount > 0) { setVisible((v) => Math.min((v ?? 0) + REVEAL_STEP, filtered.length)); return; }
    if (visible != null) setVisible((v) => v + REVEAL_STEP);
    loadMore();
  };

  const toggleType = (t) => setActiveTypes((prev) => {
    if (!prev || prev.length === 0) return [t];
    if (prev.includes(t)) return prev.filter((x) => x !== t);
    return [...prev, t];
  });

  if (status === "loading") {
    return (
      <div className="space-y-2" role="status" aria-label="Loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }
  if (status === "error") {
    return <ErrorState variant="inset" message="Couldn't load the activity stream." onRetry={() => load()} />;
  }

  // The filter tags: drawn at the top of the stream, or inside the caller's box when `filtersHost` is given.
  const tagsRow = showFilters && availableTypes.length > 0 ? (
    <div className={filtersHost ? "flex flex-wrap gap-1.5" : "flex flex-wrap gap-1.5 mb-3"}>
          {availableTypes.map((t) => {
            const active = activeTypes?.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleType(t)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  active ? "bg-[#32B1FF] text-white border-[#32B1FF]"
                    : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-800 hover:text-gray-900 dark:hover:text-white"
                }`}>
                {t}
              </button>
            );
          })}
          {activeTypes && activeTypes.length > 0 && (
            <button type="button" onClick={() => setActiveTypes(null)} data-testid="timeline-clear-tags"
              aria-label="Clear tag filters"
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800 dark:hover:text-white">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
              Clear tags
            </button>
          )}
        </div>
  ) : null;

  return (
    <div className={className} data-testid="timeline">
      {filtersHost && tagsRow ? createPortal(tagsRow, filtersHost) : null}
      {filtersHost ? null : tagsRow}
      {searching && (
        <p data-testid="timeline-search-note" className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          Searching the {events.length} loaded {events.length === 1 ? "event" : "events"}
          {nextBefore ? " — older activity is not searched." : "."}
        </p>
      )}
      {filtered.length === 0 ? (
        <EmptyState
          variant="inset"
          message={searching && events.length > 0 ? `No loaded events match “${query.trim()}”.` : emptyMessage}
          padding="py-6"
        />
      ) : (
        variant === "stream" ? (
          <ul>
            {shown.map((ev, i) => <StreamEntry key={ev.seq || i} event={ev} last={i === shown.length - 1} />)}
          </ul>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {shown.map((ev, i) => <TimelineEntry key={ev.seq || i} event={ev} />)}
          </ul>
        )
      )}
      {(hiddenCount > 0 || canFetchOlder) && (
        <div className="mt-3 flex items-center gap-2">
          {loadMoreFailed && (
            <span role="alert" className="text-sm text-red-600 dark:text-red-400" data-testid="timeline-load-more-error">
              Couldn't load older activity.
            </span>
          )}
          <button type="button" onClick={onLoadMore} disabled={loadingMore}
            className="text-sm text-[#32B1FF] hover:underline disabled:opacity-50"
            data-testid="timeline-load-more">
            {loadingMore ? "Loading…" : loadMoreFailed ? "Try again" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

